// Canonical workspace-module install-ref builder — the ONE place that shapes
// `settings.workspaceModule` and auto-grants an app its declared record
// collections. Consumed by BOTH install paths so the two can't drift:
//   • the /add-app rail        → workspace-shell `workspaceApps.js`
//   • the marketplace install  → `marketplace/functions/index.js` installWorkspaceApp
// (tickets/workspace.md WS-APPS "records-grant" ticket flagged the duplication —
// both were building the identical ref + reading the manifest independently.)
//
// Nothing else writes `capabilities.records` today (WORKSPACE_APPS.md ground
// truth), so an ungranted install would leave the module data bridge with
// nothing to allow — hence the auto-grant here.

import { fetchProjectFromSDK } from './sdk.js'
import { collectionGrantsOf } from './collectionGrantsOf.js'

// FA204 / frank trap — `_lc`/`_appId` used to live at module scope,
// referenced bare across most exports below; each export now carries its
// own self-contained copy. `recordsEntryKey`/`WS_APP_RECORD_OPS`/
// `fetchDeclaredCollections`/`isAppsVersionConflict` stay exported (grep
// confirmed real external consumers: workspaceProjects.js/
// moduleDataBridge.js/workspaceApps.js/moduleConsole.js/pages/admin/
// module.js/this file's own test suite) and get self-contained inline
// duplicates wherever an internal caller previously bare-called them.

// A `capabilities.records.collections` entry is a STRING ('bf_watch' — legacy,
// member read/write) or an OBJECT ({ key, read, write } — per-collection access
// levels; the server's AccessResolver.normalizeRecordsGrant accepts both). The
// KEY names the collection in either shape; the LEVELS are enforced server-side
// per caller, so client code only ever gates/displays by key. Every consumer
// must resolve entries through this helper — a bare String(entry) turns an
// object grant into '[object Object]' and silently revokes the collection
// client-side (the bug that froze every bellforge-app write, 2026-08-06).
export const recordsEntryKey = (e) => (typeof e === 'string' ? e : e && e.key ? String(e.key) : '')

// Append (or replace-in-place) an app ref in a `settings.workspaceApps` list,
// keyed by `owner/key` so a re-install of the same app updates its ref rather
// than duplicating it. Pure — returns a new array. This is the write that makes
// installs additive: app B lands next to app A instead of overwriting it.
export const upsertWorkspaceApp = (list, ref) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const appId = (r) => `${lc(r && r.owner)}/${lc(r && r.key)}`
  const id = appId(ref)
  const out = (Array.isArray(list) ? list : []).filter((r) => appId(r) !== id)
  out.push(ref)
  return out
}

// Remove an app from a `settings.workspaceApps` list by `owner/key`. Pure.
export const removeWorkspaceApp = (list, { owner, key }) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const appId = (r) => `${lc(r && r.owner)}/${lc(r && r.key)}`
  const target = `${lc(owner)}/${lc(key)}`
  return (Array.isArray(list) ? list : []).filter((r) => appId(r) !== target)
}

// Is this app currently installed on the workspace? True when its `owner/key`
// appears in the plural `workspaceApps` list OR equals the legacy single
// `workspaceModule` ref carrying installMode:'app' (pre-plural installs). The
// one detector both the CTA label and the install/uninstall dispatch share so
// they can't disagree.
export const isWorkspaceAppInstalled = (settings, { owner, key }) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const appId = (r) => `${lc(r && r.owner)}/${lc(r && r.key)}`
  const target = `${lc(owner)}/${lc(key)}`
  const list = settings && Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  if (list.some((r) => appId(r) === target)) return true
  const legacy = settings && settings.workspaceModule
  return !!(legacy && legacy.installMode === 'app' && appId(legacy) === target)
}

// All installed app ids (`owner/key`) on the workspace — plural list plus a
// legacy installMode:'app' workspaceModule. Deduped. Drives the installed-apps
// UI + the marketplace CTA cache.
export const installedWorkspaceAppIds = (settings) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const appId = (r) => `${lc(r && r.owner)}/${lc(r && r.key)}`
  const ids = new Set()
  const list = settings && Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  for (const r of list) if (r && r.owner && r.key) ids.add(appId(r))
  const legacy = settings && settings.workspaceModule
  if (legacy && legacy.installMode === 'app' && legacy.owner && legacy.key) ids.add(appId(legacy))
  return [...ids]
}

// The record ops a workspace app is granted over its declared collections.
export const WS_APP_RECORD_OPS = [
  'list',
  'get',
  'create',
  'update',
  'upsert',
  'remove',
  'subscribe'
]

// `ops` is 100% platform-derived — unlike `collections` (operator-curated
// via applyCollectionsSync's add/prune) or `capabilities.ai`/`files` (hand-
// granted, never derived from anything), NOTHING ever asks for a NARROWER
// op set for one app: fetchDeclaredCollections reads `key`/`read`/`write`
// off a manifest collection entry and nothing else, and no admin surface
// edits `capabilities.records.ops`. So a stored `ops` array is never a
// deliberate restriction — it is either the full current default (a fresh
// install) or a STALE snapshot frozen at whatever WS_APP_RECORD_OPS listed
// on an EARLIER install/re-sync.
//
// Both call sites below used to do `prevOps.length ? prevOps : WS_APP_RECORD_OPS`
// — "keep the old array verbatim if it's non-empty". That freezes drift
// forever: once WS_APP_RECORD_OPS grows a new op (e.g. 'subscribe', added
// for realtime records.subscribe), every ALREADY-INSTALLED app's grant stays
// pinned at whatever it had at its own install time, and re-installing never
// self-heals it, because a non-empty prevOps always won. Root-caused via
// tickets/opus.md "page EventSource misses records.change": the excalidraw
// app's natali-workspace install carried `ops:['list','get','create',
// 'update','remove']` (pre-dating 'upsert'/'subscribe'), so its shipped
// board's own `moduleData('records','subscribe',…)` call was rejected by
// moduleDataBridge's allowlist gate on EVERY mount — silently, since the
// caller's catch block assumed only "an older shell/SDK without the stream"
// — and no EventSource was ever constructed, long before any SSE-transport
// question (H2/H1.1, reconnect backoff, tab throttling, connection
// starvation) could even be exercised.
//
// Union, not override: this can only WIDEN a grant toward what a brand-new
// install of the SAME app would already receive today (no prevRef path) —
// it can never grant an op the platform doesn't already consider
// default-safe for ANY app's declared collections, so it carries no new
// security exposure. It also can't clobber a genuinely-custom future entry:
// anything already present in prevOps survives via the union even if it's
// somehow outside WS_APP_RECORD_OPS.
// FA204 / frank trap — `_freshOps` used to live at module scope and read
// the exported `WS_APP_RECORD_OPS` bare; both of its 2 callers below
// (applyCollectionsSync/buildWorkspaceModuleRef) now carry their own
// self-contained copy, each with its own literal ops array (kept in sync
// with the exported WS_APP_RECORD_OPS by hand — same 7-op list).

// Read a module project's declared record-collection entries. Returns [] on any
// miss so a manifest hiccup never blocks an install. A v2 manifest lives on
// `config` once `config.manifest === 2`, else it spreads onto the project root
// — mirrors workspaceProjects.js `_normalizeManifest`. `fetchProjectFn` is
// injectable (tests stub the network read; production defaults to the real
// SDK fetch) — mirrors the `getSdkFn` injection pattern in moduleAiBridge.js.
//
// The grant rule is the server install path's (collectionGrantsOf.js): a
// declared `access` maps case for case like the server ('read' →
// { read: 'member', write: 'admin' }, never a plain key — a plain key is
// EDITOR WRITE); an entry with no `access` that declares its own LEVELS
// (`{ key, stateKey, read, write }`) keeps them as `{ key, read, write }` so an
// install/re-install/re-sync converges to the app's declared access policy
// instead of silently reverting an operator-held object grant to member-wide
// strings. Level-less declarations stay plain key strings (legacy shape,
// member read / editor write).
// `fetchProjectFn` fallback moved off the destructuring default — frank's
// `to-json` free-var resolver doesn't walk into a destructuring default's
// expression the way it walks a body statement (same class documented on
// authGuard.js's DEFAULT_AUTH_PATHS), so the bare `fetchProjectFromSDK`
// reference here — a plain top-of-file import, already in scope — came out
// `unresolvable-identifier` instead of resolving normally.
// `branch`, `version`, `workspaceId`: the location of the version an install
// loads, named by the module console's Check for updates (the ref's version
// pin, else its channel's publish env, else the head), so the check reads the
// manifest the server checks a level approval against. Only these three ride
// next to `raw`; no option can turn `raw` off (Nika 10-31, N2 pick 1).
export const fetchDeclaredCollections = async (
  owner,
  key,
  { fetchProjectFn, branch, version, workspaceId } = {}
) => {
  const resolvedFetchProjectFn = fetchProjectFn || fetchProjectFromSDK
  const location = {}
  if (branch) location.branch = branch
  if (version) location.version = version
  if (workspaceId) location.workspaceId = workspaceId
  try {
    // `raw: true` (security review N2): a manifest read needs plain data only.
    // The default fetch REVIVES every stored project function in this realm
    // (`eval`, side effects run at revive time) - before any tier or audit
    // check, with the installing admin's session.
    const project = await resolvedFetchProjectFn(owner, key, { ...location, raw: true })
    const cfg = project && typeof project.config === 'object' ? project.config : {}
    const manifest = cfg && cfg.manifest === 2 ? cfg : project
    const cols = manifest && Array.isArray(manifest.collections) ? manifest.collections : []
    // The server install path's rule, `access` included (collectionGrantsOf.js).
    return collectionGrantsOf(cols)
  } catch (err) {
    console.warn(
      '[workspaceModuleRef] manifest fetch failed — no collection grant:',
      err && err.message
    )
    return []
  }
}

// Diff a module's CURRENTLY-declared record collections (fresh manifest read,
// via fetchDeclaredCollections) against the collections already granted on its
// stored ref. Pure — no network, no write. `added` = declared but not yet
// granted (the app now wants access it doesn't have — was permanently refused
// before this ticket); `stale` = granted but no longer declared (the app used
// to want it and still technically has read/write, though it no longer asks).
// Neither list is a grant by itself — see applyCollectionsSync. This is the
// read half of the operator-consented re-sync
// (tickets/workspace.md "a workspace app's capability grant never re-syncs").
// Both lists may mix string and `{ key, read, write }` entries — the diff
// compares by KEY and returns KEY strings (what the review UI checks and what
// applyCollectionsSync prunes by). An entry whose declared LEVELS changed but
// whose key is already granted does not diff here: the console lists it apart
// (moduleConsole mcLevelChanges), and it lands only when a workspace admin
// approves it (the settings PATCH's approveLevels) — never an implicit
// overwrite.
export const diffDeclaredCollections = (declared, granted) => {
  const entryKey = (e) => (typeof e === 'string' ? e : e && e.key ? String(e.key) : '')
  const dec = (Array.isArray(declared) ? declared : []).map(entryKey).filter(Boolean)
  const gr = (Array.isArray(granted) ? granted : []).map(entryKey).filter(Boolean)
  return {
    added: dec.filter((c) => gr.indexOf(c) < 0),
    stale: gr.filter((c) => dec.indexOf(c) < 0)
  }
}

// Apply an OPERATOR-APPROVED collections sync to one installed app's ref.
// Pure — returns a NEW ref object. Adds `add[]` to and removes `prune[]` from
// `capabilities.records.collections` ONLY. Every other key on the ref —
// `capabilities.ai`, `capabilities.files`, `tier`, `channel`, `version`,
// `installMode`, anything else an operator hand-wrote — survives via the
// spread, untouched. `add`/`prune` MUST already be the operator-checked subset
// of a diff the caller showed before calling this (mcApplyAppSync is the only
// caller) — this function itself has no opinion about which collections
// SHOULD move; it only performs the move once told.
export const applyCollectionsSync = (ref, { add = [], prune = [] } = {}) => {
  const entryKey = (e) => (typeof e === 'string' ? e : e && e.key ? String(e.key) : '')
  // Same 7-op default as the exported WS_APP_RECORD_OPS — kept as its own
  // literal (see the FA204 note above `_freshOps`'s old module-scope body).
  const defaultOps = ['list', 'get', 'create', 'update', 'upsert', 'remove', 'subscribe']
  const freshOps = (prevOps) =>
    Array.isArray(prevOps) && prevOps.length
      ? [...new Set([...prevOps, ...defaultOps])]
      : defaultOps

  const base = ref || {}
  const rec = (base.capabilities && base.capabilities.records) || {
    collections: [],
    ops: defaultOps
  }
  // Entries on the stored grant AND in `add` may be strings or
  // `{ key, read, write }` objects — prune/dedupe compare by KEY so a revoke
  // actually removes an object entry (an identity filter never matches one)
  // and an add can carry its declared levels into the grant verbatim.
  const current = Array.isArray(rec.collections) ? rec.collections : []
  const pruneSet = new Set((prune || []).map(entryKey).filter(Boolean))
  const kept = current.filter((c) => !pruneSet.has(entryKey(c)))
  const keptKeys = new Set(kept.map(entryKey))
  // Prune wins over add for the same key (in practice they're disjoint — a
  // collection is either declared-and-ungranted or granted-and-undeclared,
  // never both — but a deterministic tie-break keeps this pure fn sane
  // regardless of what a caller passes).
  const next = [...kept]
  for (const a of add || []) {
    const k = entryKey(a)
    if (!k || pruneSet.has(k) || keptKeys.has(k)) continue
    keptKeys.add(k)
    next.push(a)
  }
  return {
    ...base,
    capabilities: {
      ...(base.capabilities || {}),
      records: {
        ...rec,
        collections: next,
        ops: freshOps(rec.ops)
      }
    }
  }
}

// Build the workspace-app reference for an install, auto-granting the app its
// declared collections. `tier` defaults to 'trusted' — the server
// (WorkspaceController.validateWorkspaceModuleRef) clamps it back to
// 'sandboxed' for cross-org, non-catalog refs, so passing 'trusted' is a
// request the server is free to deny.
//
// `prevRef` (optional) is the app's EXISTING ref, if this is a re-install /
// channel-or-tier change rather than a fresh install. When it matches the
// same owner/key, its `capabilities` are PRESERVED — only `records` is
// recomputed from the fresh manifest read. Without this, re-installing an app
// that carries an operator-hand-granted `capabilities.ai` or `capabilities.
// files` (neither of which this builder derives from anything — the manifest
// has no declared-wants field for them yet) would silently DESTROY that grant
// on every re-install, since the object was rebuilt from scratch. Preserving a
// PRIOR grant is not the same as self-granting a NEW one: the security
// boundary this file exists to protect — "capabilities come from the SERVER
// ref only, never the manifest" (workspaceProjects.js:371) — is unaffected,
// because nothing here reads anything the MANIFEST declares about ai/files;
// it only carries forward what an operator already approved.
// ── Concurrency: expectedApps precondition + retry ──────────────────────────
// See tickets/server.md "workspaceApps: a stale writer can still DROP an
// entry it never saw" + server/src/domains/workspaces/services/
// WorkspaceAppsService.js for the full rationale. `PATCH
// /workspaces/:id/settings` preserves a still-installed app's stored
// capabilities — and, as of that ticket's server tightening, the STORED
// ENTRY ITSELF — unless the writer echoes back the `workspaceApps` array it
// read as `expectedApps`; a stale echo is refused with 409
// `apps_version_conflict` rather than silently reverting a grant or dropping
// an app the writer never saw. Every whole-array writer of
// `settings.workspaceApps` (workspace-shell `workspaceApps.js`, marketplace
// `installWorkspaceApp`/`uninstallMarketplaceApp` below, and this shared
// module's own consumers) uses this pair so the precondition + retry
// semantics can't drift between call sites — `moduleConsole.js`'s
// `_appsPatch`/`_rebaseApps`/`_handleSaveError` (workspace@edc817db)
// pioneered the pattern locally before it was hoisted here.

// Build the settings patch a whole-array `workspaceApps` write should send —
// the patch plus `expectedApps` echoing back the list the caller just read.
// Every caller already reads `settings.workspaceApps` immediately before
// building its patch, so this baseline is free (no extra round-trip).
export const appsWritePatch = (patch, baselineApps) => ({
  ...patch,
  expectedApps: Array.isArray(baselineApps) ? baselineApps : []
})

// Is this error the server's compare-and-swap refusal? Matches both the
// error code and the human message so it keeps matching if either changes.
export const isAppsVersionConflict = (err) =>
  /apps_version_conflict|changed since you read it/i.test((err && err.message) || '')

// Retry-once wrapper for a whole-array `workspaceApps` write. `writeFn(apps)`
// must build the desired array from a KNOWN-CURRENT `apps` list and perform
// the `sdk.updateWorkspaceSettings` write (baking `expectedApps: apps` into
// its own patch via `appsWritePatch`) — it should return whatever the caller
// needs afterward (the write response, the resolved array, both). On a 409
// conflict the caller's read was stale: re-read the workspace once and retry
// against the fresh list. A second conflict is a genuine concurrent-write
// storm, not a plain stale read, and is re-thrown rather than looped.
export const writeWorkspaceAppsWithRetry = async (sdk, wsId, currentApps, writeFn) => {
  const isVersionConflict = (err) =>
    /apps_version_conflict|changed since you read it/i.test((err && err.message) || '')
  try {
    return await writeFn(currentApps)
  } catch (err) {
    if (!isVersionConflict(err)) throw err
    const fresh = await sdk.getWorkspace(wsId).catch(() => null)
    const freshApps = (fresh && fresh.settings && fresh.settings.workspaceApps) || []
    return await writeFn(freshApps)
  }
}

export const buildWorkspaceModuleRef = async (
  { owner, key, channel, tier, prevRef },
  opts = {}
) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const appId = (r) => `${lc(r && r.owner)}/${lc(r && r.key)}`
  // Same 7-op default as the exported WS_APP_RECORD_OPS — kept as its own
  // literal (see the FA204 note above `_freshOps`'s old module-scope body).
  const defaultOps = ['list', 'get', 'create', 'update', 'upsert', 'remove', 'subscribe']
  const freshOps = (prevOps) =>
    Array.isArray(prevOps) && prevOps.length
      ? [...new Set([...prevOps, ...defaultOps])]
      : defaultOps
  // Self-contained inline copy of fetchDeclaredCollections (a bare
  // sibling-export call is exactly as unresolvable post-frank-serialization
  // as a module-local read; the named export stays live for its own real
  // consumers — workspaceProjects.js/moduleConsole.js/this file's own test
  // suite).
  const fetchDeclared = async (o, k, { fetchProjectFn } = {}) => {
    const resolvedFetchProjectFn = fetchProjectFn || fetchProjectFromSDK
    try {
      // `raw: true` (security review N2): a manifest read needs plain data only.
      // The default fetch REVIVES every stored project function in this realm
      // (`eval`, side effects run at revive time) - before any tier or audit
      // check, with the installing admin's session.
      const project = await resolvedFetchProjectFn(o, k, { raw: true })
      const cfg = project && typeof project.config === 'object' ? project.config : {}
      const manifest = cfg && cfg.manifest === 2 ? cfg : project
      // The app asks for the workspace AI (`ai: true` in its manifest). Read
      // here, in the same manifest read, and honoured below only for a system
      // catalog app.
      aiWanted.value = !!(manifest && manifest.ai === true)
      // The same for the site assistants' admin API (`assistants: true`, the
      // Assistants app): honoured below only for a system catalog app.
      assistantsWanted.value = !!(manifest && manifest.assistants === true)
      const cols = manifest && Array.isArray(manifest.collections) ? manifest.collections : []
      // The ONE client rule (collectionGrantsOf.js), the same as
      // fetchDeclaredCollections — `access` maps like the server install path.
      return collectionGrantsOf(cols)
    } catch (err) {
      console.warn(
        '[workspaceModuleRef] manifest fetch failed — no collection grant:',
        err && err.message
      )
      return []
    }
  }

  const aiWanted = { value: false }
  const assistantsWanted = { value: false }
  const collections = await fetchDeclared(owner, key, opts)
  const samePrev = prevRef && appId(prevRef) === appId({ owner, key }) ? prevRef : null
  const capabilities = { ...((samePrev && samePrev.capabilities) || {}) }
  // AI for a CATALOG app that asks for it. `capabilities.ai` used to be
  // operator-hand-written only, so a Socials install from the App Store got an
  // app whose every AI control said "AI capability not granted" and no screen
  // could grant it. A `system`-owned app is curated and trusted by
  // construction (WORKSPACE_APPS.md "Trust model"), the install itself is an
  // owner/admin act, and the server gate on /turn is workspace membership,
  // so this widens nothing a member could not already call. A third-party
  // app's `ai: true` is ignored: its grant stays the operator's decision. A
  // grant already on the ref (hand-written, or revoked to null) is kept.
  if (aiWanted.value && lc(owner) === 'system' && !Object.prototype.hasOwnProperty.call(capabilities, 'ai')) {
    capabilities.ai = { granted: 'catalog-install' }
  }
  // The assistants bridge (moduleDataBridge `assistants`) opens for a ref whose
  // capabilities.assistants is exactly true. Same trust rule as `ai` above: a
  // system catalog app that asks; a grant already on the ref is kept. The admin
  // API (fly-control) still checks the caller's own project role per assistant,
  // so this grant reveals nothing the member could not read with their session.
  if (assistantsWanted.value && lc(owner) === 'system' && !Object.prototype.hasOwnProperty.call(capabilities, 'assistants')) {
    capabilities.assistants = true
  }
  if (collections.length) {
    const prevOps = capabilities.records && capabilities.records.ops
    capabilities.records = {
      collections,
      ops: freshOps(prevOps)
    }
  } else {
    // The manifest no longer declares ANY collections — there is nothing to
    // grant on the records plane, but ai/files/etc. (if any) stay as they were.
    delete capabilities.records
  }
  return {
    type: 'project',
    owner,
    key,
    channel: channel || 'production',
    version: null,
    tier: tier || 'trusted',
    // Both consumers of this builder (marketplace install + /add-app) are
    // "add an app to my workspace" flows — the app must ADD itself (navbar
    // entries appended to the defaults, dashboard panels untouched), never
    // take the workspace over. resolveWorkspaceConfig reads this flag
    // (workspaceConfigs.js app-mode compose). Client-workspace MODULES
    // (admin module console / direct settings writes) carry no flag and
    // keep full-takeover semantics — installing crm-app used to wipe the
    // navbar down to just "Contacts" (2026-07-23).
    installMode: 'app',
    ...(Object.keys(capabilities).length ? { capabilities } : {})
  }
}
